import { stringify } from "querystring";
import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Button, Divider, Dropdown, Form, FormField, FormProps, Input, Label, List, Message, Modal, Segment, Select, Table, TextArea } from "semantic-ui-react";
import { dialogControl } from "../controls/dialog-control";
import { notificationControl } from "../controls/notification-control";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { trimSetField, useFormConnect, useFormData } from "../hooks/formHooks";
import { hostAbsoluteUrl, hostUrl, restRequest } from "../http";
import { ORDERS_NEED_COUNTER, PageData } from "../models/common_model";
import { FileSyncData } from "../models/file_sync_model";
import { fieldError, formatDateTime } from "../utils";
import { Page } from "./commons/Page";
import _ from "lodash";

export function FileSyncComponent(props: {}) {
    const currentUser = useCurrentUser();
    const [searchData, setSearchField] = useFormData<any>({});
    const [allRoles, setAllRoles] = useState<{ id: number, name: string }[]>();
    const [list, setSearchData] = useFormConnect<any, PageData<FileSyncData>>({}, 'file_sync/search');

    const editModalControl = useModalControl();
    const [currentEditId, setCurrentEditId] = useState<number>();
    const [reloadNumber, setReloadNumber] = useState(0);

    return <>
        <Segment.Group>
            <Segment attached="top">
                <Form onSubmit={e => setSearchData({ ...searchData, refresh: (searchData.refresh || 0) + 1 })}>
                    <Form.Group>
                        <Form.Field inline>
                            <Input placeholder="Name" name="name" value={searchData['name'] || ''} onChange={setSearchField} />
                        </Form.Field>

                        <div><Button icon="search" type="submit"></Button></div>
                    </Form.Group>
                </Form>
            </Segment>
            <Segment attached="bottom">
                <Table striped selectable>
                    <Table.Header>
                        <Table.Row>
                            <th>Product</th>
                            <th>Results</th>
                            <th>responsibles</th>
                            <th>Created By</th>
                            <th>Created Time</th>
                            <th>{currentUser.permissions.file_sync.create && <Button color="blue" icon="add" content="Sync" size="mini" onClick={e => {
                                setCurrentEditId(undefined);
                                editModalControl.setOpen(true);
                            }} />}</th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {list?.items.map(obj => {
                            return <tr key={obj.id}>
                                <td>{obj.product}</td>
                                <td>
                                    {currentUser.permissions.testing.view && obj.result_dropdown_options && obj.result_dropdown_options.length > 0 &&
                                        <Dropdown button className="mini" compact text="results">
                                            <Dropdown.Menu>
                                                {obj.result_dropdown_options.map(p => <Dropdown.Item key={p.id} className="project-menu-item">
                                                    <Dropdown text={p.name}>
                                                        <Dropdown.Menu>
                                                            {p.children.map(s => <Dropdown.Item key={s.id} className="project-menu-item">
                                                                <Dropdown text={s.name}>
                                                                    <Dropdown.Menu>
                                                                        {s.children.map(o => <Dropdown.Item key={o.id} as={NavLink} target="_blank" to={`/testing_visual/${o.id}/overview`}>{o.name}</Dropdown.Item>)}
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            </Dropdown.Item>)}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </Dropdown.Item>)}
                                            </Dropdown.Menu>
                                        </Dropdown>}
                                </td>
                                <td>{(obj.responsible_users || []).map(v => `${v.name} (${v.email})`).join(', ')}</td>
                                <td>{obj.author?.name}</td>
                                <td>{formatDateTime(obj.created_at)}</td>
                                <td>
                                    <Button onClick={e => {
                                        setCurrentEditId(obj.id);
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini" />

                                    {currentUser.permissions.file_sync.delete && <Button onClick={e => {
                                        dialogControl.deletionConfirm(obj.product, argee => {
                                            if (argee) {
                                                return restRequest(`file_sync/${obj.id}`, null, 'delete').then(() => {
                                                    setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
                                                })
                                            }
                                            return Promise.resolve()
                                        })
                                    }} color="red" icon="delete" size="mini" />}
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={list} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <Editor id={currentEditId} allRoles={allRoles} modelControl={editModalControl} onFinish={result => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }}></Editor>
    </>
}

function Editor(props: { id?: number, allRoles?: { id: number, name: string }[], modelControl: ModalControlData, onFinish: (data: FileSyncData) => void }) {
    const currentUser = useCurrentUser();
    const { id, allRoles, modelControl, onFinish } = props;
    const isCreate = !id;

    const [data, setField, setData] = useFormData<FileSyncData>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [allUsers, setAllUsers] = useState<{ id: number, name: string, email: string }[]>([]);
    const orderNameEditorControl = useModalControl<string>('');
    const stationNameEditorControl = useModalControl<string>('');

    useEffect(() => {
        setData({ admin: false } as any);
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<FileSyncData>('file_sync/' + id).then((data) => {
                data.responsibles = data.responsibles || [];
                setData(data);
            }).finally(() => setLoading(false))
        }
    }, [id]);

    useEffect(() => {
        restRequest<{ id: number, name: string, email: string }[]>('users/simple_list').then(setAllUsers);
    }, []);

    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        restRequest<FileSyncData>(`file_sync/${isCreate ? '' : id}`, data, isCreate ? 'post' : 'put').then(result => {
            setData(result);
            props.onFinish(result);
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }

    const folders = data.folders || [];


    return <>
        <Modal {...props.modelControl.props} size="large">
            <Modal.Header>{isCreate ? 'Create' : 'Update'} File Sync </Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>

                    <Form.Group widths="equal">
                        <Form.Input label="Product" name="product" value={data['product'] || ''} onChange={setField} onBlur={trimSetField(setField)} required error={fieldError('product', invalidError)}>
                        </Form.Input>
                    </Form.Group>
                    {isCreate || <Form.Group widths="equal">
                        <Form.Checkbox label="Regenerate Upload URL" name="regenerate_password" checked={data.regenerate_password || false} onClick={e => setField(null, { name: 'regenerate_password', value: !data.regenerate_password || false })}></Form.Checkbox>
                    </Form.Group>}
                    <Form.Group widths="equal">
                        <Form.Input label="Responsibles" error={fieldError('responsibles', invalidError)}>
                            <Dropdown multiple name="responsibles" value={data.responsibles || []} onChange={setField} options={allUsers?.map(u => ({ key: u.id, text: `${u.name} (${u.email})`, value: u.id })) || []} search selection fluid></Dropdown>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Description" error={fieldError('description', invalidError)}>
                            <TextArea name="description" value={data.description || ''} onChange={setField} />
                        </Form.Input>
                    </Form.Group>
                    <div>
                        {fieldError('folders', invalidError) && <Label basic color="red" pointing="below">Order and Station {fieldError('folders', invalidError)}</Label>}
                        <List>
                            {(currentUser.permissions.file_sync.create || currentUser.permissions.file_sync.update) && <Button as="a" color="blue" icon="add" compact content="Order (Build)" size="mini" onClick={e => {
                                e.preventDefault();

                                orderNameEditorControl.start('', (inputText: string) => {
                                    if (folders.find(f => (f.rename_order || f.order) === inputText)) {
                                        notificationControl.showError(`Order name "${inputText}" is used`);
                                        return false;
                                    }

                                    setData(d => ({ ...d, folders: [...folders, { order: inputText, stations: [] }] }));
                                    return true;
                                });
                            }} />}
                            {folders.map((folder, folderIndex) => {
                                return <List.Item key={folder.rename_order || folder.order}>
                                    {folder.rename_order || folder.order} {(currentUser.permissions.file_sync.create || currentUser.permissions.file_sync.update) && <Button onClick={e => {
                                        e.preventDefault();

                                        orderNameEditorControl.start(folder.rename_order || folder.order, (inputText: string) => {
                                            if (folders.find((f, i) => folderIndex !== i && (f.rename_order || f.order) === inputText)) {
                                                notificationControl.showError(`Order name "${inputText}" is used`);
                                                return false;
                                            }

                                            setData(d => ({
                                                ...d, folders: folders.map((f, i) => {
                                                    if (i === folderIndex) {
                                                        return { ...f, rename_order: inputText };
                                                    }

                                                    return f;
                                                })
                                            }));

                                            return true;
                                        });
                                    }} size="mini" compact icon="edit"></Button>}

                                    {/* <Button size="mini" compact icon="delete" color="red"></Button> */}

                                    {(currentUser.permissions.file_sync.create || currentUser.permissions.file_sync.update) &&  <>&nbsp;&nbsp;&nbsp;&nbsp;<Button as="a" icon="add" compact content="Testing Station" size="mini" onClick={e => {
                                        e.preventDefault();

                                        stationNameEditorControl.start('', (inputText: string) => {
                                            if (!inputText || !inputText.trim()) {
                                                notificationControl.showError("Can't be empty");

                                                return false;
                                            } else {
                                                inputText = inputText.trim();
                                            }

                                            if (folder.stations.find(s => (s[2] || s[0]) === inputText)) {
                                                notificationControl.showError(`Testing Station "${inputText}" is used`);

                                                return false;
                                            }

                                            setData(d => ({
                                                ...d, folders: folders.map((f, i) => {
                                                    if (i !== folderIndex) {
                                                        return f;
                                                    } else {
                                                        return { ...f, stations: [...f.stations, [inputText!, false, '']] };
                                                    }
                                                })
                                            }));

                                            return true;
                                        });                                    
                                    }} /></>}


                                    <List divided>
                                        {folder.stations.map((station, stationIndex) => {
                                            return <List.Item key={station[2] || station[0]}>
                                                {station[2] || station[0]} &nbsp;
                                                { (currentUser.permissions.file_sync.create || currentUser.permissions.file_sync.update) &&  <Button as="a" icon="edit" compact size="mini" onClick={e => {
                                                    e.preventDefault();

                                                    stationNameEditorControl.start(station[2] || station[0], (inputText: string) => {
                                                        if (!inputText || !inputText.trim()) {
                                                            notificationControl.showError("Can't be empty");

                                                            return false;
                                                        } else {
                                                            inputText = inputText.trim();
                                                        }

                                                        if (folder.stations.find((s, i) => stationIndex !== i && (s[2] || s[0]) === inputText)) {
                                                            notificationControl.showError(`Testing Station "${inputText}" is used`);

                                                            return false;
                                                        }

                                                        setData(d => ({
                                                            ...d, folders: folders.map((f, i) => {
                                                                if (i !== folderIndex) {
                                                                    return f;
                                                                } else {
                                                                    return {
                                                                        ...f, stations: f.stations.map((s, i) => {
                                                                            if (i === stationIndex) {
                                                                                return [s[0], s[1], inputText]
                                                                            }
                                                                            return s;
                                                                        })
                                                                    };
                                                                }
                                                            })
                                                        }));

                                                        return false;
                                                    });
                                                }} />}

                                                <List.Description style={{ display: 'inline-block', marginLeft: '2rem' }}><input type="checkbox" defaultChecked={station[1]} onChange={e => {
                                                    setData(d => ({
                                                        ...d, folders: folders.map((f, fi) => {
                                                            if (fi !== folderIndex) {
                                                                return f;
                                                            } else {
                                                                return {
                                                                    ...f, stations: f.stations.map((s, si) => {
                                                                        if (si !== stationIndex) {
                                                                            return s;
                                                                        } else {
                                                                            return [s[0], !s[1], s[2]]
                                                                        }
                                                                    })
                                                                };
                                                            }
                                                        })
                                                    }))
                                                }} style={{ verticalAlign: 'middle' }}></input>Archive</List.Description>

                                                {!isCreate && <List.Description style={{ display: 'inline-block', marginLeft: '3rem' }}>
                                                    <Button onClick={e => {
                                                        e.preventDefault();
                                                        dialogControl.alertText("Upload Url", hostAbsoluteUrl(`file_sync/__action__/${data.id}/${data.password}/${folder.order}/${station[0]}`))

                                                    }} size="mini" compact>Show Upload Url</Button>
                                                </List.Description>}

                                                {currentUser.permissions.file_sync.delete && <List.Description style={{ display: 'inline-block', marginLeft: '1rem' }}><Button as="a" onClick={e => {
                                                    dialogControl.deletionConfirm(folder.order + ' ' + station[0], argee => {
                                                        if (argee) {
                                                            const doDelete = () => {
                                                                setData(d => ({
                                                                    ...d, folders: folders.map((f, fi) => {
                                                                        if (fi !== folderIndex) {
                                                                            return f;
                                                                        } else {
                                                                            return {
                                                                                ...f, stations: f.stations.filter((s, si) => {
                                                                                    return si !== stationIndex
                                                                                })
                                                                            };
                                                                        }
                                                                    })
                                                                }))
                                                            }
                                                            if (isCreate) {
                                                                doDelete()
                                                            } else {
                                                                return restRequest(`file_sync/${data.id}/${folder.order}/${station[0]}`, null, 'delete').then(() => {
                                                                    doDelete();
                                                                })
                                                            }
                                                        }
                                                        return Promise.resolve()
                                                    })
                                                }} color="red" icon="delete" size="mini" /></List.Description>}

                                                {currentUser.permissions.file_sync.delete && !isCreate && <List.Description style={{ display: 'inline-block', marginLeft: '1rem' }}><Button as="a" onClick={e => {
                                                    dialogControl.confirm('Are you sue want to clear the data in ' + folder.order + ' ' + station[0] +'. This operation is not reversible.', argee => {
                                                        if (argee) {
                                                            return restRequest(`file_sync/${data.id}/${folder.order}/${station[0]}?clear=1`, null, 'delete');
                                                        }
                                                        return Promise.resolve()
                                                    })
                                                }} color="red" size="mini" compact>Clear</Button></List.Description>}

                                                {(isCreate || currentUser.permissions.testing.view) && <List.Description style={{ display: 'inline-block', marginLeft: '3rem' }}>
                                                    <Button onClick={e => {
                                                        e.preventDefault();
                                                        restRequest(`file_sync/testing_results/${data.id}/${folder.order}/${station[0]}`).then(resultId => {
                                                            if (resultId) {
                                                                window.open(`/testing_visual/${resultId}/overview`, '_blank');
                                                            } else {
                                                                notificationControl.showInfo('there is no testing files uploaded');
                                                            }
                                                        })

                                                    }} size="mini" compact>Visual</Button>

                                                    <Button onClick={e => {
                                                        e.preventDefault();

                                                        let port = ''
                                                        if (window.location.host.includes(':8443')) {
                                                            port = ':8443'
                                                        }
                                                        window.open(`https://production.cn.lewitt-audio.com${port}/nextcloud/apps/files/?dir=/Testing Results/${data.product}/${folder.order}/${station[0]}`, '_blank');

                                                    }} size="mini" compact>Files</Button>
                                                </List.Description>}
                                            </List.Item>
                                        })}
                                    </List>
                                </List.Item>
                            })}
                        </List>
                    </div>
                    <Divider></Divider>
                    <div>
                    {(currentUser.permissions.file_sync.create || currentUser.permissions.file_sync.update) && <Button primary>Submit</Button>}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => props.modelControl.setOpen(false)}>Cancel</Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal>

        <OrderNameEditor modalController={orderNameEditorControl} />
        <StationNameEditor modalController={stationNameEditorControl} />
    </>
}


function OrderNameEditor(props: { modalController: ModalControlData<string>}) {
    const submitBtnRef = useRef<HTMLButtonElement>(null);
    const [data, setData] = useState<{ name: string, number: string }>({} as any);
    const [availableOrderNames, setAvailableOrderNames] = useState(['TP', 'PP', 'MP', 'REWORK', 'RMA', 'RD']);
    const currentUser = useCurrentUser();

    useEffect(() => {
        if (props.modalController.data) {
            const numberMatch = props.modalController.data.match(/\d+$/);

            let name: string;
            if (numberMatch) {
                const number = numberMatch[0];
                name = props.modalController.data.replaceAll(number, '');
                setData({ name, number: number.trim() });
            } else {
                name = props.modalController.data;
                setData({ name, number: '' });
            }

            if (name && !availableOrderNames.includes(name)) {
                setAvailableOrderNames([...availableOrderNames, name]);
            }
        } else {
            setData({ name: '', number: '' });
        }
    }, [props.modalController.data]);

    return <Modal {...props.modalController.props}>
        <Modal.Content>
            <Modal.Header>Order (Build) Name</Modal.Header>
            <Form onSubmit={e => {
                e.preventDefault();
                if (e.currentTarget.checkValidity()) {
                    if (!data.name) {
                        notificationControl.showError('Order (Build) Name is required!');
                        return;
                    }
                    props.modalController.finish(data.name + (data.number || ''));
                }
            }}>
                 <Input  input={<Select disabled={!data.name || !ORDERS_NEED_COUNTER.includes(data.name)} selection options={_.range(0, 20, 1).map(c => ({ text: c || '', value: (c || '').toString() }))} value={data?.number || ''} onChange={(e, d) => setData(dd => ({ ...dd, number: d.value as string }))} style={{borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' }} />}
                    label={<Dropdown additionLabel="Custom Name: " allowAdditions={currentUser.admin} search required selection options={availableOrderNames.map(c => ({ text: c, value: c }))} value={data?.name || ''} onChange={(e, d) => {
                        if (!data.name || !ORDERS_NEED_COUNTER.includes(data.name)) {
                            setData(dd => ({ ...dd, name: d.value as string }));
                        } else {
                            setData(dd => ({ ...dd, name: d.value as string, number: '' }));
                        }
                    }} 
                    onAddItem={(e, d) => {
                        setAvailableOrderNames(dd => {
                            if (!dd.includes(d.value as string)) {
                                return [...dd, d.value as string];
                            }
                            return dd;
                        });
                        if (d.value) {
                            setData(ds => ({...ds, name: d.value as string}));
                        }
                    }
}/>
                }>
                </Input>
                <button ref={submitBtnRef} type="submit" style={{ display: 'none' }}></button>
            </Form>
        </Modal.Content>
        <Modal.Actions>
            <Button primary onClick={e => {
                e.preventDefault();
                submitBtnRef.current?.click();
            }}>Save</Button>

            <Button onClick={e => {
                e.preventDefault();
                props.modalController.setOpen(false);
            }}>Cancel</Button>
        </Modal.Actions>
    </Modal>
}


function StationNameEditor(props: { modalController: ModalControlData<string>}) {
    const submitBtnRef = useRef<HTMLButtonElement>(null);
    const [availableStationNames, setAvailableStationNames] = useState(['PCBA', 'Audioroom', 'Audiobox', 'Noise', 'Listening', 'Programming', 'Serial', 'Packaging']);
    const [name, setName] = useState('');
    const currentUser = useCurrentUser();

    useEffect(() => {
        setName(props.modalController.data);
        if (props.modalController.data && !availableStationNames.includes(props.modalController.data)) {
            setAvailableStationNames(os => [...os, props.modalController.data]);
        }
    }, [props.modalController.data]);

    return <Modal {...props.modalController.props}>
        <Modal.Content>
            <Modal.Header>Station Name</Modal.Header>
            <Form onSubmit={e => {
                e.preventDefault();
                if (e.currentTarget.checkValidity()) {
                    props.modalController.finish(name);
                }
            }}>
                <Dropdown selection additionLabel="Custom Name: " allowAdditions={currentUser.admin} search options={availableStationNames.map(c => ({ text: c, value: c }))} value={name} onChange={(e, d) => setName(d.value as string)} 
                    onAddItem={(e, d) => {
                        if (!availableStationNames.includes(d.value as string)) {
                            setAvailableStationNames(os => [...os, d.value as string]);
                        }
                        if (d.value) {
                            setName(d.value as string);
                        }
                    }} fluid/>
                <button ref={submitBtnRef} type="submit" style={{ display: 'none' }}></button>
            </Form>
        </Modal.Content>
        <Modal.Actions>
            <Button primary onClick={e => {
                e.preventDefault();
                submitBtnRef.current?.click();
            }}>Save</Button>

            <Button onClick={e => {
                e.preventDefault();
                props.modalController.setOpen(false);
            }}>Cancel</Button>
        </Modal.Actions>
    </Modal>
}